import React, { useState } from 'react';
import { Form, Button, Container } from 'react-bootstrap';
import { set } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/images/bombiix-logo.svg';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import { resetPassword, resetUserPassword, storeSetupToken } from '../../services/auth.service';
import './ResetPassword.css';
import { isPasswordCompliant } from '../../constants/password.constants';
import { backendErrorMsg, getApiErrorKey } from '../../Components/helper';
import { HTTP_STATUS } from '../../constants/api-status-codes';

/**
 * Password form for both reset flows. The signed-out reset (verifiedQuestion)
 * is authorised by the reset-link id minted by forgot-password — the server
 * refuses a reset without it — while the onboarding flow submits the current
 * password (or invitation token) and keeps the returned setup token before
 * navigating, because the 2FA screen identifies the account from that token
 * rather than from the address bar.
 * @returns the reset-password view.
 */
function ResetPassword() {
  const { t } = useTranslation('common');
  const [submitData, setSubmitData] = useState({
    currentPassword: '',
    password: '',
    confirmPassword: '',
    pageLoader: false,
  });
  const searchParams = new URLSearchParams(window?.location?.search);
  const history = useHistory();
  const email = decodeURIComponent(searchParams.get('email'));
  const verifiedQuestion = searchParams.get('verifiedQuestion');
  const resetLinkId = searchParams.get('id');
  const [isSubmitted, setIsSubmitted] = useState(false);

  // State for password visibility
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Define a state for error messages
  const [errors, setErrors] = useState({
    currentPassword: '',
    password: '',
    confirmPassword: '',
    isError: false,
  });

  const postAPI = async () => {
    if (!errors?.isError) {
      // If there are no validation errors, proceed further
      let params = {
        newPassword: submitData?.password ?? '',
        confirmPassword: submitData?.confirmPassword ?? '',
        email: email ?? '',
      };
      if (!verifiedQuestion) {
        params = { ...params, currentPassword: submitData?.currentPassword ?? "" };
      } else {
        params = { ...params, uniqueId: resetLinkId ?? "" };
      }
      setSubmitData((prevState) => ({
        ...prevState,
        pageLoader: true,
      }));
      let resData;
      try {
        resData = verifiedQuestion ? await resetUserPassword(params) : await resetPassword(params);
      } catch (error) {
        setSubmitData((prevState) => ({
          ...prevState,
          pageLoader: false,
        }));
        if (error?.response?.status !== HTTP_STATUS.TOO_MANY_REQUESTS) {
          toasterMsg(backendErrorMsg(t, getApiErrorKey(error)), 'danger');
        }
        return;
      }
      setSubmitData((prevState) => ({
        ...prevState,
        pageLoader: false,
      }));
      if (resData?.code === 200) {
        toasterMsg(t("REACT_MESSAGE.PASSWORD_SUCCESS"), "success");
        if (!verifiedQuestion && resData?.data?.setupToken) {
          storeSetupToken(resData.data.setupToken);
        }
        const url = verifiedQuestion ? "/login" : `/setup2fa?email=${encodeURIComponent(email)}`;
        history.push(url);
      } else {
        toasterMsg(backendErrorMsg(t, resData?.data?.errMsg), 'danger');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    let formError = false;
    if (!submitData?.currentPassword && !verifiedQuestion) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        currentPassword: t('REACT_MESSAGE.CPW_REQUIRED'),
        isError: true,
      }));
      formError = true;
    }

    if (!submitData?.password) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        password: t('REACT_MESSAGE.PW_REQUIRED'),
        isError: true,
      }));
      formError = true;
    } else if (!isPasswordCompliant(submitData?.password)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        isError: true,
        password: t('REACT_MESSAGE.PW_VALIDATION'),
      }));
      formError = true;
    } else {
      formError = false;
    }

    if (!submitData?.confirmPassword) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: t('REACT_MESSAGE.CN_PW_REQUIRED'),
        isError: true,
      }));
      formError = true;
    }
    if (submitData?.password !== submitData?.confirmPassword) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        confirmPassword: t('REACT_MESSAGE.PASS_MATCH_VALIDATION'),
        isError: true,
      }));
      formError = true;
    }
    if (!formError) {
      postAPI();
    }
  };

  const handleInputChange = (keyName, value) => {
    setErrors(() => ({
      password: '',
      confirmPassword: '',
      currentPassword: '',
      isError: false,
    }));
    const copyState = submitData;
    set(copyState, keyName, value);
    setSubmitData((prevState) => ({ ...prevState, ...copyState }));
  };

  // Constants for repeated strings
  const PASSWORD_EYE_OPEN = 'icon-Password-Eye-Open';
  const PASSWORD_EYE_CLOSE = 'icon-Password-Eye-Close';
  const DATA_FILLED_CLASS = 'data-filled';
  const HIDE_PASSWORD_LABEL = 'Hide password';
  const SHOW_PASSWORD_LABEL = 'Show password';

  // Toggle functions for password visibility
  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Container>
      <div className="login-container-bombiix">
        <div className="logo-container">
          <img src={logo} className="img-fluid logo-image" alt="logo" />
        </div>
        <div className="mt-40">
          <p className="mb-2 text-medium text-center">{t('RESET_PASSWORD.RESET_PASSWORD')}</p>

          <Form className="login-form" onSubmit={handleSubmit}>
            {!verifiedQuestion && (
              <Form.Group controlId="formBasicPassword">
                <Form.Label className="">
                  {t('RESET_PASSWORD.CURRENT_PASSWORD')}
                  <span className="text-danger">*</span>
                </Form.Label>
                <div className="password-input-container">
                  <Form.Control
                    type={showCurrentPassword ? 'text' : 'password'}
                    key="currentPassword"
                    id="currentPassword"
                    name="currentPassword"
                    placeholder={t('RESET_PASSWORD.CURRENT_PASSWORD')}
                    className={`password-input ${submitData?.currentPassword ? DATA_FILLED_CLASS : ''}`}
                    value={submitData?.currentPassword}
                    onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={toggleCurrentPasswordVisibility}
                    aria-label={showCurrentPassword ? HIDE_PASSWORD_LABEL : SHOW_PASSWORD_LABEL}
                  >
                    <i className={`fas ${showCurrentPassword ? PASSWORD_EYE_OPEN : PASSWORD_EYE_CLOSE}`} />
                  </button>
                </div>
                {isSubmitted && <div className="text-danger err-text">{errors?.currentPassword}</div>}
              </Form.Group>
            )}
            <Form.Group controlId="formBasicPassword1">
              <Form.Label className="">
                {t('RESET_PASSWORD.NEW_PASSWORD')}
                <span className="text-danger">*</span>
              </Form.Label>
              <div className="password-input-container">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  key="password"
                  id="password"
                  name="password"
                  placeholder={t('RESET_PASSWORD.PASSWORD')}
                  className={`password-input ${submitData?.password ? DATA_FILLED_CLASS : ''}`}
                  value={submitData?.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? HIDE_PASSWORD_LABEL : SHOW_PASSWORD_LABEL}
                >
                  <i className={`fas ${showPassword ? PASSWORD_EYE_OPEN : PASSWORD_EYE_CLOSE}`} />
                </button>
              </div>
              {isSubmitted && <div className="text-danger err-text">{errors?.password}</div>}
            </Form.Group>

            <Form.Group controlId="formBasicPassword2">
              <Form.Label>
                {t('RESET_PASSWORD.CONFIRM_PASSWORD')}
                <span className="text-danger">*</span>
              </Form.Label>
              <div className="password-input-container">
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  key="confirmPassword"
                  id="confirmPassword"
                  name="confirmPassword"
                  autoComplete="new-password"
                  placeholder={t('RESET_PASSWORD.CONFIRM_PASSWORD')}
                  className={`password-input ${submitData?.confirmPassword ? DATA_FILLED_CLASS : ''}`}
                  value={submitData?.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={toggleConfirmPasswordVisibility}
                  aria-label={showConfirmPassword ? HIDE_PASSWORD_LABEL : SHOW_PASSWORD_LABEL}
                >
                  <i className={`fas ${showConfirmPassword ? PASSWORD_EYE_OPEN : PASSWORD_EYE_CLOSE}`} />
                </button>
              </div>
              {isSubmitted && <div className="text-danger err-text">{errors?.confirmPassword}</div>}
            </Form.Group>

            <div className="d-flex justify-content-center align-items-center">
              <Button disabled={submitData.pageLoader} variant="primary" className="btn-theme" type="submit">
                {t('RESET_PASSWORD.SUBMIT')}
              </Button>
            </div>
          </Form>
        </div>
        <div className="password-length-text">{t('REACT_MESSAGE.PW_VALIDATION')}</div>
      </div>
    </Container>
  );
}

export default ResetPassword;
